/**
 * The peptide cheat sheet PDF: where it lives, what it holds and the one way to hand it over.
 *
 * Every place that offers the sheet (the homepage hero form, the download page's email gate)
 * uses this file, so they cannot drift apart again. Before 2026-09-30 the hero form sent a new
 * subscriber to the download page, whose gate ignored the subscribed flag and asked for the
 * email a second time.
 *
 * What the PDF holds, counted from scripts/build-cheatsheet-pdf.mjs (the file that builds it):
 * 11 pages, 34 single compounds and 4 blends with vial size, BAC water volume, dose and cycle,
 * a nasal spray table, a baseline bloodwork panel and a resources page. The rows themselves are
 * in src/data/cheatSheetTables.json, and src/data/cheatSheetTables.test.ts checks these counts. It has no half-life
 * data and no tiered (starter, standard, advanced) doses, so copy must not promise either.
 * Rebuild the PDF with that script whenever its content changes, and copy the result to both
 * public/BHG_Peptide_Cheat_Sheet_2026.pdf and public/downloads/.
 */
import { trackDownload } from "@/lib/analytics";

// The subscribed flag lives in subscriber.ts. Re-exported so the cheat sheet forms keep one import.
export { isSubscribed, markSubscribed } from "@/lib/subscriber";

export const CHEAT_SHEET_PDF_URL =
  (import.meta.env?.VITE_CHEAT_SHEET_PDF_URL as string | undefined) || "/downloads/BHG_Peptide_Cheat_Sheet_2026.pdf";
export const CHEAT_SHEET_PDF_FILENAME = "BHG_Peptide_Cheat_Sheet_2026.pdf";
export const CHEAT_SHEET_PAGE = "/guides/peptide-cheat-sheet-download";

/**
 * One line that says what is in the sheet. Keep it true to the PDF (see above): it lists what
 * the tables hold without promising every item for every compound (five rows have no bloodwork
 * column, three are oral and have no vial). The same sentence is written out in index.html
 * (the static hero shell), CheatSheetDownloadCTA.tsx and EmailCapture.tsx. Change all four.
 */
export const CHEAT_SHEET_SUMMARY = "38 compounds and blends: vial sizes, BAC water volumes, dosing ranges and bloodwork markers.";

/** Start the PDF download in the visitor's browser and record it. */
export function downloadCheatSheet(source: string): void {
  if (typeof document === "undefined") return;
  const a = document.createElement("a");
  a.href = CHEAT_SHEET_PDF_URL;
  a.download = CHEAT_SHEET_PDF_FILENAME;
  a.rel = "noopener noreferrer";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  // Give the browser a tick to start the download, then clean up.
  setTimeout(() => {
    if (a.parentNode) a.parentNode.removeChild(a);
  }, 100);
  trackDownload({ content_id: "peptide-cheat-sheet-2026", content_category: "guide", source });
}
